import { ActionCentre } from "components/ChannelinfoSlider/ChannelInfo/ActionCentre/ActionCentre";
import { ChatTickets } from "components/ChannelinfoSlider/ChannelInfo/ChatTickets/ChatTickets";
import { MediaDocuments } from "components/ChannelinfoSlider/ChannelInfo/MediaDocuments/MediaDocuments";
import { Info, Team } from "components/ChannelinfoSlider/ChannelinfoSlider";
import { ChatPane } from "components/ChatPane/ChatPane";
import { Button } from "components/DesignSystem/Button/Button";
import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import { UnstyledChannel } from "components/UnstyledChannel/UnstyledChannel";
import { ConditionalLink } from "components/conditionalLink";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { useEffect, useMemo, useState } from "react";
import { useChatContext } from "stream-chat-react";
import { Task1120Tracker } from "types/Models/TaskTracker";
import { globalStatusCustomOptions } from "../Task1120Columns/GlobalStatus";
import { AuditlogList } from "components/AuditlogList/AuditlogList";
import { Task1120ReviewPanel } from "./Task1120ReviewPanel";
import { Task1120AgentProcessing } from "./Task1120AgentProcessing";
import { useGetForm1120ReviewQuery } from "store/apis/Task1120Tracker";

export const Task1120SliderContent = ({
  rowData,
  defaultTab,
}: {
  rowData: Task1120Tracker;
  defaultTab: "DETAILS" | "CHAT" | "REVIEW";
}) => {
  const [tab, setTab] = useState(defaultTab);
  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);
  const [isRerunMode, setIsRerunMode] = useState(false);

  const { data: existingReview } = useGetForm1120ReviewQuery(
    { task_uuid: rowData.task_uuid },
    { skip: !!activeReviewId || !rowData.task_uuid || isRerunMode }
  );

  useEffect(() => {
    if (existingReview && !activeReviewId && !isRerunMode) {
      setActiveReviewId(existingReview.uuid);
    }
  }, [existingReview, activeReviewId, isRerunMode]);
  const { client } = useChatContext();
  const [channelId, setChannelId] = useState<string | null>(
    rowData?.channel?.channel_url
  );

  const channel = useMemo(() => {
    return client.channel("messaging", channelId);
  }, [channelId]);

  useEffect(() => {
    if (rowData?.channel?.channel_url) {
      setChannelId(rowData?.channel?.channel_url || null);
    }
  }, [rowData?.channel?.channel_url]);

  return (
    <Tab.Root
      value={tab}
      asChild
      onValueChange={(v) => setTab(v as typeof tab)}
    >
      <DashboardContainer className="t-h-full">
        <DashboardContainer.Header>
          <Slider.Header>
            <Slider.Title className="t-w-full">
              <div className="t-flex t-justify-between t-items-center t-border t-w-full t-mr-2">
                <div>
                  <div className="t-text-Subtext t-text-text-100">
                    {rowData.entity.group.name}
                  </div>
                  <div className="t-text-body-sm t-text-text-30">
                    {rowData.entity.name}
                  </div>
                </div>
                <div>
                  <Tag
                    icon={false}
                    tagType={
                      globalStatusCustomOptions[
                        rowData.task_1120_tracker_global_status
                      ].color as TagColorType
                    }
                    rounded
                  >
                    {
                      globalStatusCustomOptions[
                        rowData.task_1120_tracker_global_status
                      ].text
                    }
                  </Tag>
                </div>
              </div>
            </Slider.Title>
            <Slider.Close />
          </Slider.Header>

          <Tab.List className="t-pt-3 t-px-5">
            <Tab.Trigger value="DETAILS">Details</Tab.Trigger>
            <Tab.Trigger value="CHAT">Chat</Tab.Trigger>
            <Tab.Trigger value="ACTIVITY">Activity</Tab.Trigger>
            <Tab.Trigger value="REVIEW">Review</Tab.Trigger>
          </Tab.List>
        </DashboardContainer.Header>
        <DashboardContainer.Content className="t-h-full t-relative t-top-0">
          <Tab.Content value="DETAILS" asChild>
            <>
              <Slider.Body>
                <UnstyledChannel channel={channel}>
                  <div className="t-flex t-flex-col t-gap-4 t-pb-5 t-pt-2 t-w-full">
                    <ActionCentre groupId={rowData?.entity.group.uuid || ""} />
                    <ChatTickets />
                    <MediaDocuments
                      groupId={rowData?.entity.group.uuid || ""}
                    />
                    {/* <AuditTrail /> */}
                    <Team />
                    <Info />
                  </div>
                </UnstyledChannel>
              </Slider.Body>
              {rowData?.uuid && (
                <Slider.Footer className="!t-sticky">
                  <ConditionalLink to={`/tax/filings/${rowData?.uuid}`}>
                    <Button
                      customType="primary"
                      size="small"
                      block
                      disabled={!rowData}
                    >
                      Go to Filing
                    </Button>
                  </ConditionalLink>
                </Slider.Footer>
              )}
            </>
          </Tab.Content>

          <Tab.Content value="REVIEW" asChild>
            <Slider.Body className="t-overflow-y-auto">
              {activeReviewId ? (
                <Task1120AgentProcessing
                  reviewId={activeReviewId}
                  onRerun={() => {
                    setActiveReviewId(null);
                    setIsRerunMode(true);
                  }}
                />
              ) : (
                <Task1120ReviewPanel
                  rowData={rowData}
                  onReviewStarted={(reviewId) => {
                    setActiveReviewId(reviewId);
                    setIsRerunMode(false);
                  }}
                />
              )}
            </Slider.Body>
          </Tab.Content>

          <Tab.Content value="CHAT" asChild>
            <ChatPane channelId={channelId} setChannelId={setChannelId} />
          </Tab.Content>
          <Tab.Content value="ACTIVITY" asChild>
            <div className="t-pt-5 t-h-full">
              {rowData.entity.uuid && rowData.task_uuid && (
                <AuditlogList
                  entityId={rowData.entity.uuid}
                  objectId={rowData.task_uuid}
                />
              )}
            </div>
          </Tab.Content>
        </DashboardContainer.Content>
      </DashboardContainer>
    </Tab.Root>
  );
};
